import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { COMPANY, amountToWordsInr, formatCompanyLocality } from '@gty/common';

import { downloadDocumentPdf, printDocument } from '../api/download';
import { usePermissions } from '../auth/usePermissions';
import { WhatsAppShareButton } from '../documents/WhatsAppShareButton';
import { invoicesService, type InvoiceLineItemDto, type InvoiceRecord } from '../services';
import { cn } from '../ui/cn';
import { formatCurrency, formatShortDate } from '../ui/format';
import { Icon } from '../ui/icons';
import { PageBody } from '../ui/PageHeader';
import { LoadFailedState } from '../ui/LoadFailedState';
import { Button, EmptyState, Skeleton } from '../ui/primitives';
import { CACHE } from '../app/query-config';

/**
 * The A4 GST tax invoice — rebuilt to match `documentation/design/invoice(1).pdf`
 * exactly (layout/styling; §4 of the tech spec is the binding authority for the
 * data-binding, structure and overflow rules below).
 *
 * Laid out at a fixed 794px — A4 width at 96dpi — so what renders on screen is
 * what prints. The page scrolls horizontally on narrow viewports rather than
 * reflowing: a document that reflows is a document whose printed output no
 * longer matches its preview, which defeats the point of a print layout.
 *
 * TWO actions, ONE template. Both go through `/print/invoice/:id`:
 *   Download PDF -> `GET /invoices/:id/pdf`, rendered server-side by headless
 *                   Chrome against that route.
 *   Print        -> loads the same route in a hidden iframe and prints it.
 *
 * DYNAMIC ROWS. `<LineItemsTable>` renders exactly `invoice.lineItems.length`
 * rows — never a fixed 10-row grid (the reference PDF's 10 empty rows are an
 * unfilled template, not a target row count).
 *
 * BLANK-FIELD CONVENTION. Every blank/absent string field in the Customer
 * Detail / Invoice & Booking Detail blocks prints an em dash, never a plain
 * empty cell — one consistent convention across the whole document.
 */

const A4_WIDTH = 794;

/** `min-w-0` + `[overflow-wrap:anywhere]` — the same last-resort wrap utility
 * `ReceiptDocument`'s `FLUID` constant uses; more robust than `break-words`
 * because it can break an unbroken long token (a long email, a spaceless name).
 */
const FLUID = '[overflow-wrap:anywhere]';

/** Past this length, the widened column alone is not enough — drop one font step. */
const OVERFLOW_SHRINK_THRESHOLD = 42;
/** Past this length the value is genuinely pathological — the last-resort clamp. */
const OVERFLOW_CLIP_THRESHOLD = 520;

/**
 * The three-step overflow convention (§4.4, extended by EC-12): widen the
 * column first (done structurally below — these fields get no fixed width),
 * shrink the font if still tight, wrap as the last resort, and only clamp a
 * genuinely runaway string. Applied to customerName, customerAddress,
 * packageName, lineItem.description and settings.bookingBranch.
 */
function OverflowValue({ value }: { value: string }): JSX.Element {
  const long = value.length > OVERFLOW_SHRINK_THRESHOLD;
  const runaway = value.length > OVERFLOW_CLIP_THRESHOLD;
  return (
    <span
      className={cn(
        'block min-w-0 leading-[1.4]',
        FLUID,
        long ? 'text-[9.5px]' : 'text-[10.5px]',
        // Last resort only, mirroring ReceiptDocument.customerName: clipping one
        // field is a smaller failure than paginating the document.
        runaway && 'max-h-[58px] overflow-hidden',
      )}
    >
      {value}
    </span>
  );
}

/** `18%` when whole, `18.5%` when not — never `18.00%` (EC-17). */
function formatIgstPercent(value: number): string {
  return `${Number.isInteger(value) ? value : value.toFixed(1)}%`;
}

/* ── small building blocks ─────────────────────────────────────────────── */

/** The brand-red section strip — "the red section headers" the brief asks for. */
function SectionBar({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="bg-brand px-2.5 py-[6px] text-[9.5px] font-bold uppercase leading-none tracking-[.5px] text-white">
      {children}
    </div>
  );
}

function LabelCell({
  children,
  width = 'w-[104px]',
}: {
  children: string;
  width?: string;
}): JSX.Element {
  return (
    <td
      className={cn(
        width,
        'border border-neutral-300 bg-neutral-50 px-2 py-[5px] align-top text-[9px] font-semibold uppercase leading-[1.4] tracking-[.3px] text-neutral-600',
      )}
    >
      {children}
    </td>
  );
}

/** Every blank/absent value prints an em dash — one consistent convention (EC-18). */
function ValueCell({
  value,
  overflow,
  colSpan,
}: {
  value: string;
  overflow?: boolean;
  colSpan?: number;
}): JSX.Element {
  const printed = value.length > 0 ? value : '—';
  return (
    <td colSpan={colSpan} className="border border-neutral-300 px-2 py-[5px] align-top text-[10.5px] leading-[1.4] text-ink">
      {overflow === true ? <OverflowValue value={printed} /> : <span className={FLUID}>{printed}</span>}
    </td>
  );
}

function DetailPanel({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="overflow-hidden border border-neutral-300">
      <SectionBar>{title}</SectionBar>
      <table className="w-full table-fixed border-collapse">
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

/* ── letterhead ─────────────────────────────────────────────────────────── */

const PHONE_ICON = (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#e10600" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);
const PIN_ICON = (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#605d5d" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="mt-[2px] flex-none">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);
const MAIL_ICON = (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#605d5d" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 6-10 7L2 6" />
  </svg>
);
const GLOBE_ICON = (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#605d5d" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

/** Phone · address (2 lines) · email · website, each with a small leading icon. */
function ContactBlock(): JSX.Element {
  return (
    <div className="flex flex-col items-end gap-[5px] text-right text-[9.5px] leading-[1.4] text-ink">
      <span className="inline-flex items-center gap-1.5 font-bold">
        {PHONE_ICON}
        {COMPANY.phone}
      </span>
      <span className="inline-flex items-start gap-1.5 text-neutral-700">
        <span>
          {COMPANY.address.lines.map((line) => (
            <span key={line} className="block">
              {line}
            </span>
          ))}
          <span className="block">{formatCompanyLocality()}</span>
        </span>
        {PIN_ICON}
      </span>
      <span className="inline-flex items-center gap-1.5 text-neutral-700">
        {MAIL_ICON}
        {COMPANY.email}
      </span>
      <span className="inline-flex items-center gap-1.5 text-neutral-700">
        {GLOBE_ICON}
        {COMPANY.website}
      </span>
    </div>
  );
}

function InvoiceLetterhead(): JSX.Element {
  return (
    <header className="flex items-start justify-between gap-4 px-3 pb-3 pt-3">
      <div className="flex items-start gap-3">
        <img src="/brand/guruji-mark.png" alt="" className="h-auto w-[54px] flex-none" />
        <div>
          <div className="font-heading text-[20px] uppercase leading-[.95] tracking-[-.4px] text-brand">
            GURUJI
          </div>
          <div className="mt-[4px] text-[13px] font-bold uppercase leading-none tracking-[.8px]">
            TRAVEL YATRA
          </div>
          <div className="mt-[5px] text-[8.5px] italic leading-none text-neutral-500">
            Keep Guessing, Where&rsquo;s Next Trip?
          </div>
        </div>
      </div>
      <ContactBlock />
    </header>
  );
}

/** GSTIN / "TAX INVOICE" / "ORIGINAL FOR RECIPIENT" — one row, three columns. */
function GstinTaxInvoiceBar(): JSX.Element {
  return (
    <div className="grid grid-cols-3 items-center border-y border-ink px-3 py-[7px]">
      <span className="text-[10px] font-semibold leading-none">
        GSTIN : <span className="font-bold">{COMPANY.gstin}</span>
      </span>
      <span className="text-center font-heading text-[14px] uppercase leading-none tracking-[.5px]">
        Tax Invoice
      </span>
      <span className="text-right text-[9.5px] font-bold uppercase leading-none tracking-[.4px]">
        Original for Recipient
      </span>
    </div>
  );
}

/* ── line items ─────────────────────────────────────────────────────────── */

const TH = 'border border-neutral-300 bg-brand px-1.5 py-[6px] text-left align-middle text-[8.3px] font-bold uppercase leading-[1.2] tracking-[.2px] text-white';
const TD = 'border border-neutral-300 px-1.5 py-[5px] align-top text-[9.5px] leading-[1.35] text-ink';

function sumLineItems(rows: readonly InvoiceLineItemDto[]): {
  taxableValue: number;
  igstAmount: number;
  total: number;
} {
  return rows.reduce(
    (acc, row) => ({
      taxableValue: acc.taxableValue + row.taxableValue,
      igstAmount: acc.igstAmount + row.igstAmount,
      total: acc.total + row.total,
    }),
    { taxableValue: 0, igstAmount: 0, total: 0 },
  );
}

/** Exactly `invoice.lineItems.length` data rows — never a fixed row count (§4.5). */
function LineItemsTable({ rows }: { rows: readonly InvoiceLineItemDto[] }): JSX.Element {
  const totals = sumLineItems(rows);

  return (
    <div className="relative overflow-hidden border border-neutral-300">
      {/* Watermark — single static low-opacity image, never a repeating pattern. */}
      <img
        src="/brand/guruji-mark.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-1/2 h-[210px] w-[210px] -translate-x-1/2 -translate-y-1/2 opacity-[.04] print:opacity-[.04]"
      />
      <table className="relative w-full table-fixed border-collapse">
        <thead>
          <tr>
            <th rowSpan={2} className={cn(TH, 'w-[26px] text-center')}>
              Sr. No.
            </th>
            <th rowSpan={2} className={TH}>
              Name of Product / Service
            </th>
            <th rowSpan={2} className={cn(TH, 'w-[58px]')}>
              HSN/SAC
            </th>
            <th rowSpan={2} className={cn(TH, 'w-[36px] text-center')}>
              Qty
            </th>
            <th rowSpan={2} className={cn(TH, 'w-[62px] text-right')}>
              Rate
            </th>
            <th rowSpan={2} className={cn(TH, 'w-[76px] text-right')}>
              Taxable Value
            </th>
            <th colSpan={2} className={cn(TH, 'text-center')}>
              IGST
            </th>
            <th rowSpan={2} className={cn(TH, 'w-[72px] text-right')}>
              Total
            </th>
          </tr>
          <tr>
            <th className={cn(TH, 'w-[40px] text-center')}>%</th>
            <th className={cn(TH, 'w-[66px] text-right')}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.srNo}>
              <td className={cn(TD, 'text-center')}>{row.srNo}</td>
              <td className={TD}>
                <OverflowValue value={row.description} />
              </td>
              <td className={TD}>{row.hsnSac}</td>
              <td className={cn(TD, 'text-center')}>{row.qty}</td>
              <td className={cn(TD, 'text-right tabular-nums')}>{formatCurrency(row.rate)}</td>
              <td className={cn(TD, 'text-right tabular-nums')}>{formatCurrency(row.taxableValue)}</td>
              <td className={cn(TD, 'text-center tabular-nums')}>{formatIgstPercent(row.igstPercent)}</td>
              <td className={cn(TD, 'text-right tabular-nums')}>{formatCurrency(row.igstAmount)}</td>
              <td className={cn(TD, 'text-right font-semibold tabular-nums')}>{formatCurrency(row.total)}</td>
            </tr>
          ))}
          <tr>
            <td colSpan={5} className={cn(TD, 'text-right text-[9.5px] font-bold uppercase')}>
              Total
            </td>
            <td className={cn(TD, 'text-right font-bold tabular-nums')}>{formatCurrency(totals.taxableValue)}</td>
            <td className={TD} />
            <td className={cn(TD, 'text-right font-bold tabular-nums')}>{formatCurrency(totals.igstAmount)}</td>
            <td className={cn(TD, 'text-right font-bold tabular-nums')}>{formatCurrency(totals.total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/* ── totals · bank details · terms ─────────────────────────────────────── */

function TotalsAndTaxSummary({
  invoice,
  taxableAmount,
}: {
  invoice: InvoiceRecord;
  taxableAmount: number;
}): JSX.Element {
  const rows: ReadonlyArray<[string, number, boolean?]> = [
    ['Taxable Amount', taxableAmount],
    ['Add : IGST', invoice.gstAmount],
    ['Total Tax', invoice.gstAmount],
    ['Total Amount After Tax', invoice.total, true],
  ];

  return (
    <div className="grid grid-cols-[1fr_320px] border border-t-0 border-neutral-300">
      <div className="flex flex-col border-r border-neutral-300">
        <SectionBar>Total in words</SectionBar>
        <div className={cn('px-2.5 py-2 text-[10px] font-semibold leading-[1.4]', FLUID)}>
          {amountToWordsInr(invoice.total)}
        </div>
      </div>
      <div className="flex flex-col">
        {rows.map(([label, value, bold]) => (
          <div
            key={label}
            className={cn(
              'flex items-center justify-between border-b border-neutral-200 px-2.5 py-[6px] last:border-b-0',
              bold === true && 'bg-neutral-100',
            )}
          >
            <dt className={cn('text-[9.5px] leading-none', bold === true ? 'font-bold uppercase' : 'text-neutral-700')}>
              {label}
            </dt>
            <dd className={cn('text-[10.5px] leading-none tabular-nums', bold === true && 'font-bold')}>
              {formatCurrency(value)}
            </dd>
          </div>
        ))}
        <div className="px-2.5 py-1 text-right text-[8.5px] italic leading-none text-neutral-500">(E & O.E.)</div>
      </div>
    </div>
  );
}

function BankDetailsQrSignatory({ invoice }: { invoice: InvoiceRecord }): JSX.Element {
  const bank = invoice.settings.bank;
  const rows: ReadonlyArray<[string, string]> = [
    ['Account Holder Name', bank.accountHolderName],
    ['Bank Name', bank.bankName],
    ['Branch', bank.branch],
    ['Account No.', bank.accountNo],
    ['IFSC Code', bank.ifscCode],
    ['Account Type', bank.accountType],
    ['Google Pay / PhonePe', bank.upiMobileNumber],
    ['Name', bank.upiPayeeName],
  ];

  return (
    <div className="grid grid-cols-[1fr_220px_1fr] border border-t-0 border-neutral-300">
      <div className="border-r border-neutral-300">
        <SectionBar>Bank Details</SectionBar>
        <table className="w-full table-fixed border-collapse">
          <tbody>
            {rows.map(([label, value]) => (
              <tr key={label}>
                <td className="w-[128px] border-b border-neutral-200 px-2 py-[4px] text-[8.5px] font-semibold uppercase leading-[1.3] text-neutral-600">
                  {label}
                </td>
                <td className={cn('border-b border-neutral-200 px-2 py-[4px] text-[9.5px] font-semibold leading-[1.3] text-ink', FLUID)}>
                  {value.length > 0 ? value : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col items-center justify-center gap-2 border-r border-neutral-300 px-3 py-3">
        {invoice.settings.upiQrDataUrl.length > 0 && (
          <img src={invoice.settings.upiQrDataUrl} alt="Pay using UPI" className="h-[92px] w-[92px]" />
        )}
        <span className="text-[9px] font-semibold uppercase leading-none text-neutral-600">Pay using UPI</span>
      </div>

      <div className="flex flex-col justify-between px-3 py-3 text-center">
        <p className="text-[8.6px] leading-[1.45] text-neutral-600">
          Certified that the particulars given above are true and correct.
        </p>
        <div>
          <div className="mb-7 text-[10px] font-bold uppercase leading-none">For {COMPANY.displayName}</div>
          <div className="mx-auto w-[140px] border-t border-neutral-400 pt-1.5 text-[8.5px] uppercase leading-none tracking-[.4px] text-neutral-600">
            Authorised Signatory
          </div>
        </div>
      </div>
    </div>
  );
}

function TermsAndConditions({ items }: { items: readonly string[] }): JSX.Element {
  return (
    <div className="border border-t-0 border-neutral-300">
      <SectionBar>Terms and Conditions</SectionBar>
      <ul className="flex flex-col gap-[3px] px-3 py-2">
        {items.map((line) => (
          <li key={line} className="text-[8.5px] leading-[1.5] text-neutral-700">
            {line}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ── the document ──────────────────────────────────────────────────────── */

/** Exported so the print route renders the SAME sheet, not a copy. */
export function InvoiceSheet({ invoice }: { invoice: InvoiceRecord }): JSX.Element {
  const taxableAmount = invoice.lineItems.reduce((sum, row) => sum + row.taxableValue, 0);

  return (
    <article
      data-print-sheet
      style={{ width: A4_WIDTH }}
      className="relative flex-none overflow-hidden bg-white shadow-[0_10px_34px_rgba(0,0,0,.12)] print:shadow-none"
    >
      <div className="px-7 pb-7">
        <InvoiceLetterhead />
        <GstinTaxInvoiceBar />

        <div className="mt-3 grid grid-cols-2 gap-3">
          <DetailPanel title="Customer Detail">
            <tr>
              <LabelCell>Name</LabelCell>
              <ValueCell value={invoice.customerName} overflow />
            </tr>
            <tr>
              <LabelCell>Address</LabelCell>
              <ValueCell value={invoice.customerAddress} overflow />
            </tr>
            <tr>
              <LabelCell>Phone</LabelCell>
              <ValueCell value={invoice.customerPhone} />
            </tr>
            <tr>
              <LabelCell>GSTIN</LabelCell>
              <ValueCell value={invoice.customerGstin} />
            </tr>
            <tr>
              <LabelCell>Place of Supply</LabelCell>
              <ValueCell value={invoice.placeOfSupply} />
            </tr>
          </DetailPanel>

          <DetailPanel title="Invoice & Booking Detail">
            <tr>
              <LabelCell width="w-[86px]">Invoice No.</LabelCell>
              <ValueCell value={invoice.ref} />
              <LabelCell width="w-[86px]">Invoice Date</LabelCell>
              <ValueCell value={formatShortDate(invoice.issuedAt)} />
            </tr>
            <tr>
              <LabelCell width="w-[86px]">Booking No.</LabelCell>
              <ValueCell value={invoice.bookingRef} />
              <LabelCell width="w-[86px]">Booking Date</LabelCell>
              <ValueCell value={formatShortDate(invoice.bookingDate)} />
            </tr>
            <tr>
              <LabelCell width="w-[86px]">Journey Date</LabelCell>
              <ValueCell value={formatShortDate(invoice.travelDate)} />
              <LabelCell width="w-[86px]">Return Date</LabelCell>
              <ValueCell value={formatShortDate(invoice.returnDate)} />
            </tr>
            <tr>
              <LabelCell width="w-[86px]">Booking Branch</LabelCell>
              <ValueCell value={invoice.settings.bookingBranch} overflow />
              <LabelCell width="w-[86px]">Payment Mode</LabelCell>
              <ValueCell value={invoice.paymentMode ?? ''} />
            </tr>
            <tr>
              <LabelCell width="w-[86px]">Package Name</LabelCell>
              <ValueCell value={invoice.packageName} overflow colSpan={3} />
            </tr>
          </DetailPanel>
        </div>

        <div className="mt-3">
          <LineItemsTable rows={invoice.lineItems} />
        </div>

        <TotalsAndTaxSummary invoice={invoice} taxableAmount={taxableAmount} />
        <BankDetailsQrSignatory invoice={invoice} />
        <TermsAndConditions items={invoice.settings.termsAndConditions} />
      </div>
    </article>
  );
}

/* ── page shell ────────────────────────────────────────────────────────── */

export default function InvoiceDocumentPage(): JSX.Element {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const { allows } = usePermissions();

  const {
    data: invoice,
    isPending,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['invoices', 'detail', invoiceId],
    queryFn: ({ signal }) => invoicesService.get(invoiceId ?? '', signal),
    ...CACHE.record,
    enabled: invoiceId !== undefined,
  });

  if (isPending) {
    return (
      <PageBody>
        <Skeleton className="h-8 w-40" />
        <Skeleton className="mx-auto mt-4 h-[1000px] w-[794px]" />
      </PageBody>
    );
  }

  // A failed fetch is not a missing invoice — see `LoadFailedState`.
  if (isError) {
    return (
      <LoadFailedState
        noun="invoice"
        error={error}
        onRetry={() => void refetch()}
        isRetrying={isFetching}
        backTo="/invoices"
        backLabel="Back to Invoices"
      />
    );
  }

  if (invoice === null || invoice === undefined) {
    return (
      <EmptyState
        title="Invoice not found"
        description="That invoice number does not match anything in the system."
        action={
          <Link to="/invoices">
            <Button variant="outline">Back to Invoices</Button>
          </Link>
        }
      />
    );
  }

  return (
    <div className="animate-gy-in px-5 pb-[46px] pt-6 md:px-8 print:p-0">
      <div className="print:hidden">
        <Link
          to="/invoices"
          className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold leading-none text-neutral-600 hover:text-ink"
        >
          <Icon name="chevron-left" size={14} />
          Invoices
        </Link>

        <div className="mt-4 flex flex-wrap items-center gap-4">
          <div>
            <div className="text-[11.5px] font-medium uppercase leading-none tracking-caps text-neutral-500">
              Finance · Invoice
            </div>
            <h1 className="mt-2 text-[22px] font-heading leading-none tracking-tight">
              {invoice.ref}
            </h1>
          </div>
          <div className="ml-auto flex flex-wrap gap-2.5">
            {/* Download returns a server-rendered file; Print goes to paper. */}
            <Button
              variant="outline"
              disabled={downloading}
              onClick={() => {
                setDownloading(true);
                void downloadDocumentPdf('invoices', invoice.id, `${invoice.ref}.pdf`)
                  .catch((err: unknown) => {
                    setDownloadError(
                      err instanceof Error ? err.message : 'Could not generate the PDF.',
                    );
                  })
                  .finally(() => {
                    setDownloading(false);
                  });
              }}
            >
              {downloading ? 'Preparing…' : 'Download PDF'}
            </Button>
            <Button variant="outline" onClick={() => { void printDocument('invoices', invoice.id).catch(() => undefined); }}>
              Print
            </Button>
            {/*
              Was a permanently-disabled placeholder ("Sharing ships with
              Notifications") predating this feature. "Send via WhatsApp" IS
              the sharing feature that placeholder was waiting on — replacing
              it rather than adding a second, competing share control.
            */}
            <WhatsAppShareButton
              kind="invoices"
              documentId={invoice.id}
              label="Invoice"
              phone={invoice.customerPhone}
              fileName={`${invoice.ref}.pdf`}
            />
            {allows('PAYMENTS_RECEIPTS', 'FULL') && (
              <Button disabled title="Payment capture ships with Payments & Receipts">
                Record Payment
              </Button>
            )}
          </div>
        </div>

        {downloadError !== null && (
        <div role="alert" className="mt-4 rounded-[10px] border border-danger-border bg-danger-bg px-4 py-3 text-[12.5px] leading-[1.5] text-danger-ink">{downloadError}</div>
      )}

      <div className="my-5 h-0.5 bg-ink" />
      </div>

      {/* Fixed-width sheet; scrolls rather than reflowing so preview == print. */}
      <div className="overflow-x-auto pb-4 print:overflow-visible print:pb-0">
        <div className="mx-auto w-fit">
          <InvoiceSheet invoice={invoice} />
        </div>
      </div>
    </div>
  );
}
